import { useMemo } from 'react';
import type { GanttTask } from '../../types';

interface GanttChartProps {
  tasks: GanttTask[];
  onTaskClick?: (task: GanttTask) => void;
}

const COLORS = [
  '#3B82F6', // blue
  '#10B981', // green
  '#F59E0B', // amber
  '#EF4444', // red
  '#8B5CF6', // violet
  '#EC4899', // pink
  '#06B6D4', // cyan
  '#F97316', // orange
];

function getDefaultColor(index: number): string {
  return COLORS[index % COLORS.length];
}

function formatDate(dateStr: string): string {
  const date = new Date(dateStr);
  return date.toLocaleDateString('it-IT', { day: '2-digit', month: 'short' });
}

function daysBetween(start: Date, end: Date): number {
  return Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
}

export function GanttChart({ tasks, onTaskClick }: GanttChartProps) {
  // Calculate chart boundaries
  const { minDate, maxDate, totalDays } = useMemo(() => {
    if (tasks.length === 0) {
      const today = new Date();
      const nextMonth = new Date(today);
      nextMonth.setMonth(nextMonth.getMonth() + 1);
      return {
        minDate: today,
        maxDate: nextMonth,
        totalDays: 30
      };
    }

    const dates = tasks.flatMap(t => [new Date(t.dataInizio), new Date(t.dataFine)]);
    const min = new Date(Math.min(...dates.map(d => d.getTime())));
    const max = new Date(Math.max(...dates.map(d => d.getTime())));

    // Add padding
    min.setDate(min.getDate() - 3);
    max.setDate(max.getDate() + 3);

    return {
      minDate: min,
      maxDate: max,
      totalDays: daysBetween(min, max)
    };
  }, [tasks]);

  // Generate week markers
  const weekMarkers = useMemo(() => {
    const markers: { date: Date; label: string; position: number }[] = [];
    const current = new Date(minDate);

    // Start from first Monday
    current.setDate(current.getDate() + (8 - current.getDay()) % 7);

    while (current <= maxDate) {
      const position = (daysBetween(minDate, current) / totalDays) * 100;
      markers.push({
        date: new Date(current),
        label: formatDate(current.toISOString()),
        position
      });
      current.setDate(current.getDate() + 7);
    }

    return markers;
  }, [minDate, maxDate, totalDays]);

  // Calculate task positions
  const taskBars = useMemo(() => {
    return tasks.map((task, index) => {
      const start = new Date(task.dataInizio);
      const end = new Date(task.dataFine);

      const left = (daysBetween(minDate, start) / totalDays) * 100;
      const width = (daysBetween(start, end) / totalDays) * 100;
      const color = task.colore || getDefaultColor(index);

      return {
        task,
        left: Math.max(0, left),
        width: Math.max(2, Math.min(width, 100 - left)),
        color
      };
    });
  }, [tasks, minDate, totalDays]);

  if (tasks.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow p-8 text-center">
        <p className="text-gray-500">Nessuna attività pianificata</p>
        <p className="text-sm text-gray-400 mt-2">Aggiungi la prima attività per visualizzare il diagramma</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow overflow-hidden">
      {/* Header with timeline */}
      <div className="border-b bg-gray-50 px-4 py-2">
        <div className="relative h-6">
          {weekMarkers.map((marker, i) => (
            <div
              key={i}
              className="absolute text-xs text-gray-500 -translate-x-1/2"
              style={{ left: `${marker.position}%` }}
            >
              {marker.label}
            </div>
          ))}
        </div>
      </div>

      {/* Task rows */}
      <div className="divide-y">
        {taskBars.map(({ task, left, width, color }) => (
          <div
            key={task.id}
            className="flex items-center hover:bg-gray-50 cursor-pointer transition-colors"
            onClick={() => onTaskClick?.(task)}
          >
            {/* Task name */}
            <div className="w-48 flex-shrink-0 px-4 py-3 border-r">
              <div className="font-medium text-sm text-gray-900 truncate">
                {task.nome}
              </div>
              <div className="text-xs text-gray-500">
                {formatDate(task.dataInizio)} - {formatDate(task.dataFine)}
              </div>
            </div>

            {/* Gantt bar */}
            <div className="flex-1 px-4 py-2 relative h-14">
              {/* Grid lines */}
              {weekMarkers.map((marker, i) => (
                <div
                  key={i}
                  className="absolute top-0 bottom-0 border-l border-gray-100"
                  style={{ left: `${marker.position}%` }}
                />
              ))}

              {/* Task bar */}
              <div
                className="absolute top-2 h-10 rounded-md flex items-center px-2 transition-all"
                style={{
                  left: `${left}%`,
                  width: `${width}%`,
                  backgroundColor: color,
                  minWidth: '20px'
                }}
              >
                {/* Progress indicator */}
                <div
                  className="absolute inset-0 bg-white/30 rounded-md origin-left"
                  style={{
                    transform: `scaleX(${task.percentuale / 100})`,
                  }}
                />

                {/* Percentage text */}
                {width > 8 && (
                  <span className="relative text-xs font-medium text-white z-10">
                    {task.percentuale}%
                  </span>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Legend */}
      <div className="border-t bg-gray-50 px-4 py-2 flex items-center gap-4 text-xs text-gray-600">
        <span>Progresso: parte chiara = completato</span>
        <span>|</span>
        <span>Click su un'attività per modificarla</span>
      </div>
    </div>
  );
}
